import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { loginUser, useAppDispatch } from "../redux";
import MainLogo from "../components/common/MainLogo";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ReCAPTCHA from "react-google-recaptcha";
import { validateForm } from "../utils/validation";
import { showToast } from "../utils/toast";
import EmailVerificationRequired from "../components/auth/EmailVerificationRequired";
import { setUser } from "../redux/slices/authSlice";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_BASE_URL = (() => {
  const raw = process.env.REACT_APP_API_BASE_URL || "https://api.ibrlive.com";
  // Allow env to be either "https://api.ibrlive.com" OR "https://api.ibrlive.com/api"
  return raw.replace(/\/+$/, "").replace(/\/api$/i, "");
})();

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    recaptcha: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerificationRequired, setShowVerificationRequired] =
    useState(false);
  const [userEmail, setUserEmail] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getPostLoginPath = useCallback((fallback = "/dashboard") => {
    const redirect = searchParams.get("redirect");
    if (
      redirect &&
      redirect.startsWith("/") &&
      !redirect.startsWith("//") &&
      !redirect.toLowerCase().startsWith("/login")
    ) {
      return redirect;
    }
    return fallback;
  }, [searchParams]);

  const handleGoogleResponse = useCallback(
    async (response) => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken: response.credential }),
        });

        const data = await res.json();
        if (!res.ok || data.error) {
          console.error("Google login failed:", data.error || res.statusText);
          showToast.error(
            data.error || "Google login failed. Please try again."
          );
          return;
        }

        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        if (data.user) {
          localStorage.setItem("userData", JSON.stringify(data.user));
          dispatch(setUser(data.user));
        }

        const selectedPlanRaw = localStorage.getItem("selectedPlan");
        if (selectedPlanRaw) {
          localStorage.removeItem("selectedPlan");
          const planData = JSON.parse(selectedPlanRaw);
          navigate("/dashboard/subscription", {
            replace: true,
            state: {
              selectedPlan: planData.planName,
              billingCycle: planData.billingCycle,
              currency: planData.currency,
              autoSelect: true,
              customApiRequests: planData.customApiRequests || null,
            },
          });

          showToast.success(
            "Login successful! Redirecting to your selected plan..."
          );
        } else {
          const isAdmin = data.user?.role === "admin";
          navigate(
            getPostLoginPath(isAdmin ? "/admin-dashboard" : "/dashboard"),
            {
              replace: true,
            }
          );
          showToast.success("Login successful with Google!");
        }
      } catch (err) {
        console.error("Google login failed:", err);
        showToast.error("Google login failed. Please try again.");
      }
    },
    [dispatch, navigate, getPostLoginPath]
  );

  useEffect(() => {
    if (!window.google || !GOOGLE_CLIENT_ID) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });

    const btn = document.getElementById("googleSignInDiv");
    if (btn) {
      window.google.accounts.id.renderButton(btn, {
        theme: "outline",
        size: "large",
      });
    }
  }, [handleGoogleResponse]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Clear previous errors
    setErrors({});

    // Validate form
    const validation = validateForm(formData, "login");

    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        email: true,
        password: true,
        recaptcha: true,
      });
      setLoading(false);
      return;
    }

    try {
      const result = await dispatch(
        loginUser({
          email: formData.email.trim(),
          password: formData.password,
          recaptcha: formData.recaptcha,
        })
      ).unwrap();

      // Check if email verification is required
      if (result.requiresVerification) {
        setUserEmail(formData.email);
        setShowVerificationRequired(true);
        setLoading(false);
        return;
      }

      // Check if user came from pricing page with a plan selection
      const selectedPlan = localStorage.getItem("selectedPlan");

      if (selectedPlan) {
        // Clear the stored plan selection
        localStorage.removeItem("selectedPlan");
        const planData = JSON.parse(selectedPlan);

        // Navigate to dashboard subscription tab with the selected plan
        navigate("/dashboard/subscription", {
          state: {
            selectedPlan: planData.planName,
            billingCycle: planData.billingCycle,
            currency: planData.currency,
            autoSelect: true,
            customApiRequests: planData.customApiRequests || null,
          },
        });

        showToast.success(
          "Login successful! Redirecting to your selected plan..."
        );
      } else if (result.shouldNavigate) {
        // Prefer ?redirect= when present (e.g. API Playground)
        const isAdmin = result.redirectTo === "/admin-dashboard";
        navigate(
          getPostLoginPath(isAdmin ? "/admin-dashboard" : result.redirectTo)
        );
        showToast.success("Login successful!");
      }
    } catch (error) {
      console.error("Login failed:", error);

      // Handle email verification requirement
      if (error.requiresVerification) {
        setUserEmail(formData.email);
        setShowVerificationRequired(true);
        setLoading(false);
        return;
      }
      console.log(error, "error");
      showToast.error(error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerificationRequired(false);
    setUserEmail("");
    setErrors({});
    setTouched({});
    setFormData({
      email: "",
      password: "",
      recaptcha: "",
    });
  };

  if (showVerificationRequired) {
    return (
      <>
        <Header />
        <EmailVerificationRequired
          email={userEmail}
          onBackToLogin={handleBackToLogin}
        />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div
        className="container-fluid auth-container"
        style={{
          minHeight: "calc(100vh - 200px)",
          paddingTop: "10rem",
          paddingBottom: "8rem",
        }}
      >
        <div
          className="container d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <div
            className="card p-4 shadow-lg"
            style={{ width: "100%", maxWidth: "400px" }}
          >
            <div className="d-flex justify-content-center mb-2">
              <MainLogo />
            </div>
            <h3 className="text-center mb-4">Login</h3>

            {/* Show message if user came from pricing */}
            {localStorage.getItem("selectedPlan") && (
              <div className="alert alert-info text-center mb-3">
                Please login to continue with your plan selection
              </div>
            )}

            <form onSubmit={handleLogin}>
              {/* Email */}
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${
                    touched.email && errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading}
                />
                {touched.email && errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className={`form-control ${
                      touched.password && errors.password ? "is-invalid" : ""
                    }`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
              </div>

              <div className="d-flex justify-content-end mb-3">
                <Link
                  to="/request-password-reset"
                  className="text-decoration-none"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* reCAPTCHA - reserve space to avoid layout shift */}
              <div
                className="mb-3 d-flex justify-content-center"
                style={{ minHeight: "90px", alignItems: "center" }}
              >
                <ReCAPTCHA
                  sitekey="6LcME8krAAAAAJ6WuRag6iHZQXn0o2GK8Iw7DtSE"
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, recaptcha: value }))
                  }
                  onExpired={() =>
                    setFormData((prev) => ({ ...prev, recaptcha: "" }))
                  }
                  onError={(error) => {
                    console.error("reCAPTCHA error:", error);
                    showToast.error(
                      "reCAPTCHA failed to load. Please refresh the page."
                    );
                  }}
                  theme="light"
                  size="normal"
                />
                {touched.recaptcha && errors.recaptcha && (
                  <div className="text-danger mt-1 text-center w-100">
                    <small>{errors.recaptcha}</small>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </button>

              <div className="mt-3">
                <span className="text-muted small d-block mb-2 text-center">
                  Or continue with
                </span>
                {/* Google Sign-In button wrapper */}
                <div
                  id="googleSignInDiv"
                  style={{ display: "flex", justifyContent: "center" }}
                />
              </div>
            </form>

            <div className="text-center mt-3">
              <small className="text-muted">
                Don't have an account? <Link to="/register">Register</Link>
              </small>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Login;
